import { Component, Input, OnInit, forwardRef } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormBuilder,
    UntypedFormControl,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DocumentsDivisionDefaultValues } from 'app/interfaces/documents/documentsDivisionDefaultValues';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListDivisionDefaultValuesRes } from 'app/interfaces/responses/Exposed/exposedListDivisionDefaultValuesRes';
import { ExposedListEmployeeDivisionsRes } from 'app/interfaces/responses/Exposed/exposedListEmployeeDivisionsRes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';

export interface DocumentsAddGeneralInfoValue {
    title: string | null;
    description: string | null;
    documentTypeId: number | null;
    documentCategoryId: number | null;
    documentSubcategoryId: number | null;
    submitToStafraentIsland: boolean | null;
    submitToPortalLSR: boolean | null;
    publicationDate: Date | null;
    svid: string | null;
    deild: string | null;
    documentSystemId: number | null;
    documentDate: Date | null;
}

@Component({
    selector: 'documents-add-general-info',
    templateUrl: './documents-add-general-info.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DocumentsAddGeneralInfoComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DocumentsAddGeneralInfoComponent),
            multi: true,
        },
    ],
})
export class DocumentsAddGeneralInfoComponent implements OnInit, ControlValueAccessor {
    //#region Globals

    @Input() includeSystem = true;
    @Input() includeDocumentDate = true;
    todayDate: Date = new Date();
    descriptionSingleDocTextHTML: string;

    categoriesDropDown: Array<DropDownItem> = [];
    subcategoriesDropDown: Array<DropDownItem> = [];
    systemsDropDown: Array<DropDownItem> = [];
    typesDropDown: Array<DropDownItem> = [];
    svidDropDown: Array<DropDownItem> = [];
    deildirDropDown: Array<DropDownItem> = [];
    descriptionSingleDocEditor = ClassicEditor;
    svid: string[] = [];
    deildir: string[] = [];
    userDetails: ExposedReadUserDetailsRes = null;
    divisionDefaultValuesRes: ExposedListDivisionDefaultValuesRes[];
    localStorageDivisionDefaultValues: DocumentsDivisionDefaultValues;

    form: FormGroup;

    //#endregion

    //#region ControlValueAccessor

    onChange: any = () => {};
    onTouched: any = () => {};

    writeValue(obj: any): void {
        if (obj) {
            this.form.patchValue(obj, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.form.disable() : this.form.enable();
    }

    validate(): ValidationErrors | null {
        return this.form.valid
            ? null
            : {
                  invalidForm: {
                      valid: false,
                      message: 'generalInfo fields are invalid',
                  },
              };
    }

    //#endregion

    //#region Init

    constructor(
        private route: ActivatedRoute,
        private _fuseConfirmationService: FuseConfirmationService,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        public configsService: ConfigsService,
        private lsrauthService: LsrauthService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.getLocalStorageDefaultValues();
        this.initForm();
        this.initDropdowns();
        this.getConfigsAndInitForm();
    }

    initDropdowns() {
        var [categoriesRes, subcategoriesRes, systemsRes, typesRes, departmentsRes, documentTemplates, employeeDivisions] =
            this.route.snapshot.parent.data.dropdownData;

        categoriesRes.forEach((x) => {
            this.categoriesDropDown.push({
                value: x.documentCategoryId.toString(),
                viewValue: x.title,
            });
        });
        subcategoriesRes.forEach((x) => {
            this.subcategoriesDropDown.push({
                value: x.documentSubcategoryId.toString(),
                viewValue: x.title,
            });
        });
        systemsRes.forEach((x) => {
            this.systemsDropDown.push({
                value: x.documentSystemId.toString(),
                viewValue: x.title,
            });
        });
        typesRes.forEach((x) => {
            this.typesDropDown.push({
                value: x.documentTypeId.toString(),
                viewValue: x.title,
            });
        });

        employeeDivisions.forEach((x: ExposedListEmployeeDivisionsRes) => {
            this.svidDropDown.push({
                value: x.name,
                viewValue: x.name,
            });
        });

        departmentsRes.forEach((x: any) => {
            this.deildirDropDown.push({
                value: x,
                viewValue: x,
            });
        });
    }

    getConfigsAndInitForm() {
        this.configsService.getDivisionDefaultValues().subscribe((res) => {
            this.divisionDefaultValuesRes = res;
            this.initForm();
        });
    }

    initForm() {
        this.descriptionSingleDocTextHTML = '';
        this.descriptionSingleDocEditor = ClassicEditor;
        this.form = this.formBuilder.group({
            title: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl(),
            documentTypeId: [null, [Validators.required]],
            documentCategoryId: [null, [Validators.required]],
            documentSubcategoryId: new UntypedFormControl(),
            submitToStafraentIsland: new UntypedFormControl(false),
            submitToPortalLSR: new UntypedFormControl(true),
            publicationDate: new UntypedFormControl(this.todayDate, [Validators.required]),
            svid: new UntypedFormControl('', [Validators.required]),
            deild: new UntypedFormControl(),
            defaultValue: new UntypedFormControl('None'),
        });

        if (this.includeSystem) {
            this.form.addControl('documentSystemId', new FormControl(null, [Validators.required]));
        }

        if (this.includeDocumentDate) {
            this.form.addControl('documentDate', new FormControl(this.todayDate, [Validators.required]));
        }

        if (this.localStorageDivisionDefaultValues != undefined && this.localStorageDivisionDefaultValues.defaultValueSelection !== null) {
            let lastSelection = this.localStorageDivisionDefaultValues.defaultValueSelection;
            this.form.get('defaultValue').setValue(lastSelection);
            this.changeDefaultValues(this.form.get('defaultValue').value);
        }

        this.form.valueChanges.subscribe((value) => {
            this.onChange(value);
        });

        // Ensure the form is correctly populated with data from local storage.
        // If this method is not called, the form remains empty unless updated through the front end.
        this.form.updateValueAndValidity();
        this.onChange(this.form.value);
    }

    //#endregion

    //#region Prefilled values (local storage and division based)

    getLocalStorageDefaultValues() {
        if (localStorage.getItem('documentsDivisionDefaultValues') != null) {
            this.localStorageDivisionDefaultValues = JSON.parse(localStorage.getItem('documentsDivisionDefaultValues'));
        }
    }

    changeDefaultValues(selectionValue) {
        let documentType = null;
        let documentSystem = null;
        let documentCategory = null;
        let documentSubcategory = null;
        let division = null;
        let department = null;

        this.form.get('documentTypeId').setValue(documentType);
        if (this.includeSystem) {
            this.form.get('documentSystemId').setValue(documentSystem);
        }
        this.form.get('documentCategoryId').setValue(documentCategory);
        this.form.get('documentSubcategoryId').setValue(documentSubcategory);
        this.form.get('svid').setValue(division);
        this.form.get('deild').setValue(department);

        if (selectionValue == 'LastSelection') {
            // Fetch user selection from local storage.
            try {
                documentType = this.localStorageDivisionDefaultValues.documentType;
                documentSystem = this.localStorageDivisionDefaultValues.documentSystem;
                documentCategory = this.localStorageDivisionDefaultValues.documentCategory;
                documentSubcategory = this.localStorageDivisionDefaultValues.documentSubcategory;
                division = this.localStorageDivisionDefaultValues.division;
                department = this.localStorageDivisionDefaultValues.department;

                if (documentType != null) {
                    this.form.get('documentTypeId').setValue(this.typesDropDown.find((x) => x.value == documentType).value);
                }
                if (documentSystem != null && this.includeSystem) {
                    this.form.get('documentSystemId').setValue(this.systemsDropDown.find((x) => x.value == documentSystem).value);
                }
                if (documentCategory != null) {
                    this.form.get('documentCategoryId').setValue(this.categoriesDropDown.find((x) => x.value == documentCategory).value);
                }
                if (documentSubcategory != null) {
                    this.form
                        .get('documentSubcategoryId')
                        .setValue(this.subcategoriesDropDown.find((x) => x.value == documentSubcategory).value);
                }
                if (division != null) {
                    this.form.get('svid').setValue(this.svidDropDown.find((x) => x.value == division).viewValue);
                }
                if (department != null) {
                    this.form.get('deild').setValue(this.deildirDropDown.find((x) => x.value == department).viewValue);
                }
            } catch {
                // Preventing an error if a document was added via impersonation access.
            }
        } else if (selectionValue == 'YourDivision' && this.divisionDefaultValuesRes != undefined) {
            // Fetch division default values from confings.
            let keyList = this.divisionDefaultValuesRes.map((x) => x.key);

            if (keyList.includes('DocumentType')) {
                documentType = this.divisionDefaultValuesRes.find((x) => x.key == 'DocumentType').value;
                this.form.get('documentTypeId').setValue(this.typesDropDown.find((x) => x.viewValue == documentType).value);
            }
            if (this.includeSystem && keyList.includes('DocumentSystem')) {
                documentSystem = this.divisionDefaultValuesRes.find((x) => x.key == 'DocumentSystem').value;
                this.form.get('documentSystemId').setValue(this.systemsDropDown.find((x) => x.viewValue == documentSystem).value);
            }
            if (keyList.includes('DocumentCategory')) {
                documentCategory = this.divisionDefaultValuesRes.find((x) => x.key == 'DocumentCategory').value;
                this.form.get('documentCategoryId').setValue(this.categoriesDropDown.find((x) => x.viewValue == documentCategory).value);
            }
            if (keyList.includes('DocumentSubcategory')) {
                documentSubcategory = this.divisionDefaultValuesRes.find((x) => x.key == 'DocumentSubcategory').value;
                this.form
                    .get('documentSubcategoryId')
                    .setValue(this.subcategoriesDropDown.find((x) => x.viewValue == documentSubcategory).value);
            }
            if (keyList.includes('Division')) {
                division = this.divisionDefaultValuesRes.find((x) => x.key == 'Division').value;
                this.form.get('svid').setValue(this.svidDropDown.find((x) => x.viewValue == division).viewValue);
            }
            if (keyList.includes('Department')) {
                department = this.divisionDefaultValuesRes.find((x) => x.key == 'Department').value;
                this.form.get('deild').setValue(this.deildirDropDown.find((x) => x.viewValue == department).viewValue);
            }
        }
    }

    //#endregion

    //#region Other / Helper methods

    public descriptionSingleDocOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionSingleDocTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    islandConfirmRead(event) {
        if (event.checked) {
            let configForm = this.formBuilder.group({
                title: 'Ísland.is - Notkunarleiðbeiningar LSR',
                message:
                    'Ég staðfesti að ég hafi kynnt mér <a class="c-link" target="_blank" href="https://lsr.atlassian.net/wiki/spaces/rekstrarkerfi/pages/123568129/2.32.2+Notkun+p+sth+lfs">leiðbeiningar LSR</a> um notkun stafræns pósthólfs Ísland.is',
                icon: {
                    show: true,
                    name: 'heroicons_outline:check',
                    color: 'primary',
                },
                actions: {
                    confirm: {
                        show: true,
                        label: 'Staðfesta',
                        color: 'primary',
                    },
                    cancel: {
                        show: true,
                        label: 'Hætta við',
                    },
                },
                dismissible: true,
            });

            // Open the dialog and save the reference of it
            const dialogRef = this._fuseConfirmationService.open(configForm.value);

            const self = this;

            // Subscribe to afterClosed from the dialog reference
            dialogRef.afterClosed().subscribe((result) => {
                if (result == 'confirmed') {
                    this.form.get('submitToStafraentIsland').setValue(true);
                } else {
                    this.form.get('submitToStafraentIsland').setValue(false);
                }
            });
        }
    }

    //#endregion
}
